import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useBlacklist } from '../../hooks/useBlacklist';
import { useToast } from '../../context/ToastContext';
import { ADMIN_KEY_STORE } from '../../config/blacklist';
import { shortAddr } from '../../utils/format';

export default function Blacklist() {
  const bl = useBlacklist();
  const toast = useToast();

  const [secret, setSecret] = useState(() => localStorage.getItem(ADMIN_KEY_STORE) || '');
  const [type, setType] = useState('address'); // 'address' | 'id'
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const saveSecret = (v) => {
    setSecret(v);
    localStorage.setItem(ADMIN_KEY_STORE, v);
  };

  const validate = () => {
    const v = value.trim();
    if (type === 'address') {
      if (!ethers.utils.isAddress(v)) throw 'Enter a valid wallet address.';
      return ethers.utils.getAddress(v);
    }
    if (!/^\d+$/.test(v)) throw 'Enter a valid user ID.';
    return v;
  };

  const add = async () => {
    if (!secret) { toast.error('Enter the admin key first.'); return; }
    let v;
    try { v = validate(); } catch (e) { toast.error(typeof e === 'string' ? e : 'Invalid value.'); return; }
    setBusy(true);
    try {
      await bl.write('add', type, v, secret);
      toast.success('Added to blacklist.');
      setValue('');
    } catch (e) {
      toast.error(e.message || 'Failed to add.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t, v) => {
    if (!secret) { toast.error('Enter the admin key first.'); return; }
    setBusy(true);
    try {
      await bl.write('remove', t, String(v), secret);
      toast.success('Removed from blacklist.');
    } catch (e) {
      toast.error(e.message || 'Failed to remove.');
    } finally {
      setBusy(false);
    }
  };

  const total = bl.addresses.length + bl.ids.length;

  return (
    <div>
      <div className="section-title"><span className="bar" />Blacklist</div>

      {/* Admin key */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>Admin Key</div>
        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
          Required to add/remove. Stored only in this browser, never published.
        </div>
        <div className="field">
          <input
            type="password"
            placeholder="Enter admin key"
            value={secret}
            onChange={(e) => saveSecret(e.target.value)}
          />
        </div>
      </div>

      {/* Add form */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5 }}>Block a User</div>
        <div className="seg" style={{ position: 'static', margin: '12px 0 0' }}>
          <button className={type === 'address' ? 'on' : ''} onClick={() => setType('address')}>By Address</button>
          <button className={type === 'id' ? 'on' : ''} onClick={() => setType('id')}>By User ID</button>
        </div>
        <div className="field">
          <input
            placeholder={type === 'address' ? '0x…' : '1234'}
            value={value}
            onChange={(e) => setValue(type === 'id' ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={add} disabled={busy}>
            {busy ? <span className="spin-inline" /> : null}
            {busy ? 'Saving…' : 'Add to Blacklist'}
          </button>
        </div>
      </div>

      {/* Current list */}
      <div className="card">
        <div className="card-row" style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Blacklisted ({total})</span>
          <button className="pill" style={{ cursor: 'pointer' }} onClick={bl.refresh}>Refresh</button>
        </div>

        {!bl.loaded ? (
          <div className="empty">Loading…</div>
        ) : total === 0 ? (
          <div className="empty">No blacklisted users.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {bl.ids.map((id) => (
              <Row key={`id-${id}`} label={`#${id}`} tag="ID" onRemove={() => remove('id', id)} busy={busy} />
            ))}
            {bl.addresses.map((a) => (
              <Row key={`a-${a}`} label={shortAddr(a)} tag="ADDR" onRemove={() => remove('address', a)} busy={busy} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, tag, onRemove, busy }) {
  return (
    <div className="card-row" style={{ padding: '10px 12px', border: '1px solid var(--glass-brd)', borderRadius: 12 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="pill blue">{tag}</span>
        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</span>
      </span>
      <button
        className="pill"
        style={{ cursor: 'pointer', color: 'var(--err)', borderColor: 'rgba(255,90,95,0.4)', opacity: busy ? 0.5 : 1 }}
        onClick={onRemove}
        disabled={busy}
      >
        Remove
      </button>
    </div>
  );
}
